/**
 * NeonLikeButton - Video game style neon like button with counter
 * Links to Facebook page and tracks likes
 */

import { useState, useEffect } from 'react';
import { Heart, ThumbsUp, Zap, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const NeonLikeButton = ({ 
  facebookUrl = "https://www.facebook.com/GTSRsports/",
  className = "" 
}) => {
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [particles, setParticles] = useState([]);

  // Load likes from localStorage on mount
  useEffect(() => {
    const storedLikes = localStorage.getItem('grindtime_likes');
    const userLiked = localStorage.getItem('grindtime_user_liked');
    
    if (storedLikes) {
      setLikes(parseInt(storedLikes, 10));
    } else {
      // Start with a base number
      setLikes(1247);
    }
    
    if (userLiked === 'true') {
      setHasLiked(true);
    }
  }, []);

  // Create particle explosion effect
  const createParticles = () => {
    const newParticles = [];
    for (let i = 0; i < 12; i++) {
      newParticles.push({
        id: Date.now() + i,
        angle: (360 / 12) * i,
        distance: 40 + Math.random() * 30,
        size: 4 + Math.random() * 6,
        color: Math.random() > 0.5 ? '#00f3ff' : '#ff007f',
        delay: Math.random() * 0.2
      });
    }
    setParticles(newParticles);
    setTimeout(() => setParticles([]), 800);
  };

  const handleLike = () => {
    if (!hasLiked) {
      // Increment likes
      const newLikes = likes + 1;
      setLikes(newLikes);
      setHasLiked(true);
      
      // Save to localStorage
      localStorage.setItem('grindtime_likes', newLikes.toString());
      localStorage.setItem('grindtime_user_liked', 'true');
      
      // Trigger animations
      setIsAnimating(true);
      createParticles();
      
      setTimeout(() => setIsAnimating(false), 600);
      
      toast.success("Thanks for the love! 💙", {
        duration: 2000
      });
    }
    
    // Open Facebook page
    window.open(facebookUrl, '_blank');
  };

  return (
    <div className={`relative inline-flex flex-col items-center gap-2 ${className}`} data-testid="neon-like-button">
      {/* Particle Effects */}
      {particles.map(particle => (
        <div
          key={particle.id}
          className="absolute pointer-events-none animate-particle-explode"
          style={{
            '--angle': `${particle.angle}deg`,
            '--distance': `${particle.distance}px`,
            '--delay': `${particle.delay}s`,
            width: particle.size,
            height: particle.size,
            borderRadius: '50%',
            backgroundColor: particle.color,
            boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`,
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            animationDelay: `${particle.delay}s`
          }}
        />
      ))}

      {/* Main Button */}
      <button
        onClick={handleLike}
        className={`
          relative group
          px-8 py-4 
          rounded-xl
          font-orbitron font-bold text-lg
          transition-all duration-300
          transform hover:scale-105 active:scale-95
          ${hasLiked 
            ? 'bg-gradient-to-r from-neon-pink via-purple-500 to-neon-blue' 
            : 'bg-gradient-to-r from-neon-blue via-purple-600 to-neon-pink'
          }
          border-2 border-transparent
          hover:border-white/30
          shadow-lg shadow-neon-pink/30
          hover:shadow-xl hover:shadow-neon-blue/50
          overflow-hidden
        `}
        style={{
          animation: isAnimating ? 'pulse-neon 0.3s ease-in-out 2' : 'none'
        }}
      >
        {/* Animated Background Glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-neon-blue/20 via-transparent to-neon-pink/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Scanning Line Effect */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        </div>

        {/* Button Content */}
        <div className="relative flex items-center gap-3">
          {/* Heart Icon with animation */}
          <div className={`relative ${isAnimating ? 'animate-bounce' : ''}`}>
            <Heart 
              className={`w-7 h-7 transition-all duration-300 ${
                hasLiked 
                  ? 'fill-white text-white drop-shadow-[0_0_8px_rgba(255,0,127,0.8)]' 
                  : 'text-white group-hover:fill-white/50'
              }`}
            />
            {/* Glow ring on liked */}
            {hasLiked && (
              <div className="absolute inset-0 animate-ping">
                <Heart className="w-7 h-7 text-neon-pink opacity-50" />
              </div>
            )}
          </div>

          {/* Text */}
          <span className="text-white drop-shadow-[0_0_10px_rgba(0,243,255,0.5)]">
            {hasLiked ? 'LIKED!' : 'LIKE US'}
          </span>

          {/* Sparkle effect */}
          <Sparkles className={`w-5 h-5 text-yellow-300 ${isAnimating ? 'animate-spin' : 'animate-pulse'}`} />
        </div>

        {/* Corner Accents */}
        <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-neon-blue rounded-tl" />
        <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-neon-pink rounded-tr" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-neon-pink rounded-bl" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-neon-blue rounded-br" />
      </button>

      {/* Likes Counter */}
      <div className="relative">
        {/* Counter Background */}
        <div className="
          relative
          px-6 py-2
          bg-black/80 backdrop-blur-sm
          rounded-full
          border border-neon-blue/50
          shadow-lg shadow-neon-blue/20
          overflow-hidden
        ">
          {/* Animated border */}
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(0,243,255,0.3), transparent)',
              animation: 'shimmer 2s infinite'
            }}
          />
          
          {/* Counter Content */}
          <div className="relative flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-400 animate-pulse" />
            <span className="font-orbitron text-sm">
              <span className={`
                text-transparent bg-clip-text bg-gradient-to-r from-neon-blue to-neon-pink
                font-bold text-lg
                ${isAnimating ? 'animate-pulse' : ''}
              `}>
                {likes.toLocaleString()}
              </span>
              <span className="text-gray-400 ml-1 text-xs">LIKES</span>
            </span>
          </div>
        </div>

        {/* Floating +1 animation */}
        {isAnimating && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 animate-float-up">
            <span className="font-orbitron text-xl font-bold text-neon-green drop-shadow-[0_0_10px_rgba(0,255,100,0.8)]">
              +1
            </span>
          </div>
        )}
      </div>

      {/* Facebook Badge */}
      <div className="flex items-center gap-2 mt-1 opacity-70 hover:opacity-100 transition-opacity">
        <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
        <span className="font-rajdhani text-xs text-gray-400">Follow us on Facebook</span>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes pulse-neon {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(255, 0, 127, 0.5), 0 0 40px rgba(0, 243, 255, 0.3);
          }
          50% { 
            box-shadow: 0 0 40px rgba(255, 0, 127, 0.8), 0 0 60px rgba(0, 243, 255, 0.5);
          }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes float-up {
          0% { 
            opacity: 1; 
            transform: translate(-50%, 0) scale(1);
          }
          100% { 
            opacity: 0; 
            transform: translate(-50%, -30px) scale(1.5);
          }
        }
        
        .animate-float-up {
          animation: float-up 0.8s ease-out forwards;
        }
        
        .animate-particle-explode {
          animation: particle-explode 0.6s ease-out forwards;
        }
        
        @keyframes particle-explode {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(
              calc(-50% + cos(var(--angle)) * var(--distance)),
              calc(-50% + sin(var(--angle)) * var(--distance))
            ) scale(0);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

// Compact version for navbar/header
export const NeonLikeButtonCompact = ({ 
  facebookUrl = "https://www.facebook.com/GTSRsports/",
  className = "" 
}) => {
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);

  useEffect(() => {
    const storedLikes = localStorage.getItem('grindtime_likes');
    const userLiked = localStorage.getItem('grindtime_user_liked');
    
    if (storedLikes) {
      setLikes(parseInt(storedLikes, 10));
    } else {
      setLikes(1247);
    }
    
    if (userLiked === 'true') {
      setHasLiked(true);
    }
  }, []);

  const handleLike = () => {
    if (!hasLiked) {
      const newLikes = likes + 1;
      setLikes(newLikes);
      setHasLiked(true);
      localStorage.setItem('grindtime_likes', newLikes.toString());
      localStorage.setItem('grindtime_user_liked', 'true');
    }
    window.open(facebookUrl, '_blank');
  };

  return (
    <button
      onClick={handleLike}
      className={`
        flex items-center gap-2 px-4 py-2
        bg-gradient-to-r from-neon-blue/20 to-neon-pink/20
        hover:from-neon-blue/30 hover:to-neon-pink/30
        border border-neon-pink/50 hover:border-neon-pink
        rounded-full
        transition-all duration-300
        group
        ${className}
      `}
      data-testid="neon-like-button-compact"
    >
      <Heart 
        className={`w-4 h-4 transition-all ${
          hasLiked 
            ? 'fill-neon-pink text-neon-pink' 
            : 'text-neon-pink group-hover:fill-neon-pink/50'
        }`}
      />
      <span className="font-orbitron text-xs text-white">{likes.toLocaleString()}</span>
    </button>
  );
};

export default NeonLikeButton;
